/**
 * Color configuration for AmazonEmbededView component
 * Centralizes all color values used throughout the component tree
 */

// Amazon Theme (Teal/Orange)
const amazonTheme = {
  // Primary/Brand Colors
  primary: {
    main: "#007185", // Main brand color (teal)
    hover: "#005f6f", // Darker teal for hover states
    dark: "#00576B", // Dark teal variant
    light: "rgba(0, 113, 133, 0.125)", // Light teal with opacity (#00718520)
    background: "rgba(0, 87, 107, 0.04)", // Very light teal background
  },

  // Accent Colors
  accent: {
    orange: "#EC6337", // Orange/coral accent color
  },

  // Text Colors
  text: {
    primary: "#0F1111", // Primary dark text
    secondary: "#555555", // Secondary gray text
    tertiary: "#717680", // Lighter gray text
    disabled: "#9E9E9E", // Disabled/inactive text
    white: "#FFFFFF", // White text
  },

  // Background Colors
  background: {
    white: "#FFFFFF", // Pure white
    lightGray: "#F8F8F8", // Very light gray
    gray: "#F5F5F5", // Light gray
    mediumGray: "#F0F0F0", // Medium gray
    darkGray: "#EFEFEF", // Slightly darker gray
    darkerGray: "#E5E5E5", // Darker gray
  },

  // Border Colors
  border: {
    light: "#E0E0E0", // Light gray border
    divider: "#D5D9D9", // Divider gray
    medium: "#d3d3d3", // Medium gray border
    dark: "#C9C9C9", // Darker gray border
  },

  // Status Colors - Success
  success: {
    background: "#E9FBEF", // Light green background
    border: "#87E8AD", // Green border
    text: "#047647", // Dark green text
    icon: "#047647", // Green icon
    positive: "#0b7b3c", // Positive change indicator
  },

  // Status Colors - Error/Fail
  error: {
    background: "#F2DEDE", // Light red background
    alertBackground: "#FFE5E5", // Alert red background
    border: "#A72E2C", // Red border
    text: "#A94442", // Dark red text
    icon: "#A94442", // Red icon
    negative: "#c10015", // Negative change indicator
    input: "#d32f2f", // Input error border
  },

  // Status Colors - Warning
  warning: {
    background: "#FFF4D9", // Light yellow background
    text: "#DAA520", // Gold text
    icon: "#856404", // Darker yellow/brown icon
  },

  // Status Colors - Info
  info: {
    background: "#EDF6FC", // Light blue background
    text: "#036AA3", // Blue text
  },

  // Toggle Button Colors
  toggle: {
    activeBackground: "rgba(0, 102, 192, 0.1)", // Light blue when toggled
    activeBorder: "none", // No border when active
    inactiveBackground: "#FFFFFF", // White when inactive
    inactiveBorder: "1px solid #E0E0E0", // Gray border when inactive
    activeText: "#00576B", // Dark teal when toggled
    inactiveText: "#9E9E9E", // Light gray when off
    hoverActive: "rgba(0, 102, 192, 0.15)", // Hover state for active
    hoverInactive: "#F5F5F5", // Hover state for inactive
    pressedActive: "rgba(0, 102, 192, 0.2)", // Pressed state for active
    pressedInactive: "#EEEEEE", // Pressed state for inactive
  },

  // Gradient Colors
  gradient: {
    primary: "linear-gradient(90deg, #00BFFF 0%, #9370DB 100%)", // Primary gradient
  },

  // Default/Fallback Colors
  default: {
    background: "#FFFFFF", // Default white background
    text: "#000000", // Default black text
  },
} as const;

// Main App Theme (Blue)
const mainAppTheme = {
  // Primary/Brand Colors
  primary: {
    main: "#0F56CA", // Main brand color (blue)
    hover: "#155eef", // Lighter blue for hover states
    dark: "#0a3d8f", // Dark blue variant
    light: "rgba(15, 86, 202, 0.125)", // Light blue with opacity
    background: "rgba(15, 86, 202, 0.04)", // Very light blue background
  },

  // Accent Colors
  accent: {
    orange: "#d18f00", // Orange/gold accent (pending status)
  },

  // Text Colors
  text: {
    primary: "#333333", // Primary dark text
    secondary: "#484848", // Secondary gray text
    tertiary: "#8c8c8c", // Lighter gray text (subheading)
    disabled: "#d8d8d8", // Disabled/inactive text
    white: "#FFFFFF", // White text
  },

  // Background Colors
  background: {
    white: "#FFFFFF", // Pure white
    lightGray: "#fafafa", // Very light gray
    gray: "#f3f3f3", // Light gray (table header)
    mediumGray: "#E9E9EA", // Medium gray (switch track)
    darkGray: "#FAFAFB", // Slightly darker gray
    darkerGray: "#181D1F15", // Darker gray with opacity
  },

  // Border Colors
  border: {
    light: "#CCCCCC", // Light gray border (default)
    divider: "#dcdddd", // Divider gray (table border)
    medium: "#86b7fe", // Medium blue border (focused/hover)
    dark: "#0000001f", // Darker border with opacity
  },

  // Status Colors - Success
  success: {
    background: "#E9FBEF", // Light green background
    border: "#87E8AD", // Green border
    text: "#11843c", // Dark green text (status)
    icon: "#11843c", // Green icon
    positive: "#11843c", // Positive change indicator
  },

  // Status Colors - Error/Fail
  error: {
    background: "#FFE5E5", // Light red background
    alertBackground: "#FFE5E5", // Alert red background
    border: "#c50000", // Red border
    text: "#c50000", // Dark red text (rejected status)
    icon: "#c50000", // Red icon
    negative: "#c50000", // Negative change indicator
    input: "#c50000", // Input error border
  },

  // Status Colors - Warning
  warning: {
    background: "#FFF4D9", // Light yellow background
    text: "#DAA520", // Gold text
    icon: "#d18f00", // Darker orange icon (pending)
  },

  // Status Colors - Info
  info: {
    background: "#EBF6FF", // Light blue background
    text: "#0F56CA", // Blue text
  },

  // Toggle Button Colors
  toggle: {
    activeBackground: "rgba(15, 86, 202, 0.1)", // Light blue when toggled
    activeBorder: "none", // No border when active
    inactiveBackground: "#FFFFFF", // White when inactive
    inactiveBorder: "1px solid #CCCCCC", // Gray border when inactive
    activeText: "#0F56CA", // Blue when toggled
    inactiveText: "#8c8c8c", // Light gray when off
    hoverActive: "rgba(15, 86, 202, 0.15)", // Hover state for active
    hoverInactive: "#f3f3f3", // Hover state for inactive
    pressedActive: "rgba(15, 86, 202, 0.2)", // Pressed state for active
    pressedInactive: "#E9E9EA", // Pressed state for inactive
  },

  // Gradient Colors
  gradient: {
    primary: "linear-gradient(90deg, #0F56CA 0%, #155eef 100%)", // Blue gradient
  },

  // Default/Fallback Colors
  default: {
    background: "#FFFFFF", // Default white background
    text: "#333333", // Default dark text
  },
} as const;

// Active theme - change this to switch themes
// Options: amazonTheme, mainAppTheme
export const colors = amazonTheme;

// Export both themes for reference
export { amazonTheme, mainAppTheme };

// Type helper for color access
export type Colors = typeof colors;

export default colors;

